import React, { useMemo, useCallback, useState } from 'react';
import './EmployeesList.scss';
import { FeatureToggle } from '../../../components/FeatureToggle/FeatureToggle';
import { useClient } from '../../../hooks/useClient';
import { User } from '../../../types/user';
import { useOrganizationContext } from '../../../context/OrganizationContext';
import useSWR from 'swr';
import { Button } from 'primereact/button';
import { UserPanel } from '../../../components/UserPanel/UserPanel';
import { ServerResponsiveTable } from '../../../components/ServerResponsiveTable/ServerResponsiveTable';
import { TableColumn, TableAction } from '../../../types/responsiveTable';
import { useNavigateScroll } from '../../../hooks/useNavigateScroll';
import { PrimeIcons } from 'primereact/api';
import { ItemDisplay } from '../../../types/responses';
import { ALL_FEATURES } from '../../../config/features';
import { Chip } from '../../../components/Chip/Chip';
import { hasFeaturesPermission } from '../../../utils/featureUtils';
import { useAuthContext } from '../../../context/AuthContext';
import { confirmDialog } from 'primereact/confirmdialog';
import classNames from 'classnames';
import { ExportButton } from '../../../components/ExportButton/ExportButton';

const getTableColumns = (): TableColumn<User>[] => [
  {
    label: '',
    accessor: 'avatarUrl',
    type: 'avatar',
  },
  {
    label: 'Nombre',
    accessor: 'name',
    type: 'string',
    isTitle: true,
    valueFromCallBack: (name, row) => `${name} ${row.surnames}`,
    renderSuffix: (name, row) => (row.isInvitation ? <Chip>Pendiente</Chip> : null),
  },
  {
    label: 'Rol',
    accessor: 'role',
    valueFromCallBack: (role) => [role.name],
    type: 'dropdown',
    hideBulletPoints: true,
  },
  {
    label: 'Sucursales',
    accessor: 'branches',
    type: 'dropdown',
    valueFromCallBack: (branches: ItemDisplay[]) => branches?.map((branch) => branch?.name) || '',
  },
  {
    label: 'Estado',
    accessor: 'isActive',
    textKeyFromCallBack: (isActive: boolean) => (isActive ? 'Activo' : 'Inactivo'),
    colorFromCallBack: (isActive: boolean) => {
      return isActive ? 'success' : 'danger';
    },
    type: 'tag',
  },
];

export const EmployeesList = () => {
  const { get, patch } = useClient();
  const navigate = useNavigateScroll();
  const { organization, organizationLoading } = useOrganizationContext();
  const { features, featuresLoading, user: currentUser } = useAuthContext();

  const [panelVisible, setPanelVisible] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<string | undefined>();
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const { data: selectedResource } = useSWR(selectedUserId ? `/users/${selectedUserId}` : null, get<User>);

  const openPanel = useCallback((editing: boolean, id?: string) => {
    setIsEditing(editing);
    setSelectedUserId(id);
    setPanelVisible(true);
  }, []);

  const closePanel = useCallback(() => {
    setPanelVisible(false);
    setSelectedUserId(undefined);
  }, []);

  const handleToggleStatus = useCallback(
    async (id: string | undefined) => {
      if (!id || !organization?.id) return false;
      setIsTogglingStatus(true);

      const user = await get<User>(`/users/${id}`);
      const isActive = user?.isActive;
      const action = isActive ? 'baja' : 'alta';

      const response = await patch(`/users/${id}/toggle-active`, {
        successMessage: {
          summary: `Se ha dado de ${action} a ${user?.name} ${user?.surnames}`,
          detail: `El empleado se ha dado de ${action} correctamente`,
        },
        errorMessages: {
          summary: `No se ha podido dar de ${action} a ${user?.name} ${user?.surnames}`,
          defaultDetail: `No se ha podido dar de ${action} al usuario`,
          USER_HAS_FUTURE_RESERVATIONS: `El empleado tiene reservas futuras. No se puede dar de baja hasta que se hayan cancelado.`,
          USER_IS_SUPERVISOR:
            'El empleado es supervisor de algún cliente. No se le puede dar de baja hasta que no tenga ningún cliente asignado.',
        },
        handlers: {
          defaultSuccess: () => {
            closePanel();
          },
        },
      });

      setIsTogglingStatus(false);
      return !!response;
    },
    [organization, patch, get, closePanel],
  );

  const showToggleStatusDialog = useCallback(
    async (id: string) => {
      const user = await get<User>(`/users/${id}`);
      const isActive = user?.isActive;
      const action = isActive ? 'baja' : 'alta';

      confirmDialog({
        message: (
          <>
            ¿Estás seguro de que quieres dar de {action} a{' '}
            <b>
              {user?.name} {user?.surnames}
            </b>
            ?
          </>
        ),
        header: `Confirmar ${action}`,
        icon: PrimeIcons.EXCLAMATION_CIRCLE,
        acceptLabel: `Sí, dar de ${action}`,
        rejectLabel: 'No, volver',
        accept: () => handleToggleStatus(id),
        acceptClassName: classNames('p-button-outlined', {
          'p-button-danger': isActive,
          'p-button-success': !isActive,
        }),
        defaultFocus: 'reject',
        draggable: false,
        resizable: false,
      });
    },
    [get, handleToggleStatus],
  );

  const userHasEditFeature = hasFeaturesPermission([ALL_FEATURES.USERS_WRITE], { features, featuresLoading });

  const tableHeaders: TableColumn<User>[] = useMemo(() => getTableColumns(), []);

  // Creamos las opciones base
  const baseOptions: TableAction[] = useMemo(
    () => [
      {
        label: 'Ver',
        onClick: (id) => {
          openPanel(false, id);
        },
        hasPermission: hasFeaturesPermission([ALL_FEATURES.USERS], { features, featuresLoading }),
      },
      {
        label: 'Editar',
        onClick: (id) => {
          openPanel(true, id);
        },
        hasPermission: userHasEditFeature,
      },
    ],
    [features, featuresLoading, openPanel, userHasEditFeature],
  );

  // Añadimos la opción de dar de baja/alta solo si no es el usuario actual
  const options = useMemo(() => {
    const result = [...baseOptions];

    if (userHasEditFeature) {
      result.push({
        label: (id: string) => {
          return 'Dar de baja/alta';
        },
        onClick: (id) => {
          if (id === currentUser?.id) return;
          showToggleStatusDialog(id);
        },
        hasPermission: (id: string) => {
          return id !== currentUser?.id && userHasEditFeature;
        },
      });
    }

    return result;
  }, [currentUser?.id, userHasEditFeature, baseOptions, showToggleStatusDialog]);

  const allowedFeatures = useMemo(() => {
    return {
      edit: [ALL_FEATURES.USERS_WRITE],
      create: [ALL_FEATURES.USERS_WRITE],
      delete: [ALL_FEATURES.USERS_WRITE],
    };
  }, []);

  const renderHeaderOptions = useCallback(
    (fetchAllForExport: () => Promise<User[]>) => (
      <div className='employees-header-options'>
        <FeatureToggle featuresToCheck={[ALL_FEATURES.USERS_WRITE]}>
          <Button
            label='Añadir empleado'
            outlined
            rounded
            icon={PrimeIcons.PLUS}
            onClick={() => {
              openPanel(false);
            }}
          />
        </FeatureToggle>
        <FeatureToggle featuresToCheck={[ALL_FEATURES.ROLES]}>
          <Button
            label='Ver roles'
            outlined
            text
            size='large'
            onClick={() => {
              navigate('/employees/roles');
            }}
          />
        </FeatureToggle>
        <ExportButton fetchData={fetchAllForExport} columns={tableHeaders} fileName='Empleados' disabled={isTogglingStatus} />
      </div>
    ),
    [openPanel, navigate, tableHeaders, isTogglingStatus],
  );

  return (
    <>
      <ServerResponsiveTable
        tableId='employees'
        endpoint={organization ? `/organizations/${organization.id}/users/paginated` : ''}
        columns={tableHeaders}
        options={options}
        disabled={isTogglingStatus}
        headerOptions={renderHeaderOptions}
        onRowClick={(row) => {
          openPanel(false, row.id);
        }}
      />
      <UserPanel
        resource={selectedResource}
        visible={panelVisible}
        onHide={closePanel}
        onIsEditingChange={setIsEditing}
        onDeleteResource={handleToggleStatus}
        isDeleting={isTogglingStatus}
        isEditing={isEditing}
        allowedFeatures={allowedFeatures}
      />
    </>
  );
};
